import { useState, useEffect, useCallback, useRef } from "react";
import log from "logbeacon";
import { deviceDetector, getItem, setItem, getDeviceType } from "utils";
import { useSnapshot } from "valtio";
import { useMemoizedFn } from "ahooks";
import Switch from "../Switch";
import { SwapModal } from "../swap/SwapModal";

import Translations from "@translate/components/Setting/NotificationSection";
import { I18nUtil } from "@utils/i18n";
import { UserStore } from "@/src/store/modules/user";
import { LOCAL_STORAGE_KEYS } from "@/src/config";
import {
  useSubscribe,
  useNotificationPermission,
  useAppPushSubscription,
} from "@/src/hooks";
import { sleep } from "@/src/utils/sleep";

const I18n = I18nUtil.createScoped(Translations);

interface NotificationSectionProps {
  /** "inline" 仅渲染 toggle 开关（用于嵌入 ProfileRow trailing） */
  variant?: "card" | "inline";
}

export const NotificationSection: React.FC<NotificationSectionProps> = ({
  variant = "card",
}) => {
  const [enableAppPushSetting, setEnableAppPushSetting] = useState(false);
  const [appOpenStatus, setAppOpenStatus] = useState(false);

  const isApp = deviceDetector.isApp();

  const { getPushSetting } = useSubscribe();
  const { openAppPush, closeAppPush } = useAppPushSubscription();
  const settingRef = useRef(false);
  const { data: notificationPermission, requestNotificationPermission } =
    useNotificationPermission();

  // 根据系统通知状态 更新 App 推送状态
  const updateNotificationPermission = useCallback(
    async (notificationPermission = { enabled: true }) => {
      if (!deviceDetector.isApp()) {
        return;
      }
      const response = await getPushSetting();
      const currentDevice = getDeviceType();
      const openStatus =
        response?.data?.data
          ?.find((item) => item.name === "agent_agent")
          ?.device_status?.find(
            (item) =>
              item.device_type?.toLowerCase() === currentDevice.toLowerCase(),
          )?.status === "OPEN";
      setAppOpenStatus(openStatus && notificationPermission?.enabled);
      log.debug(
        "[NotificationSection] closeAppPush",
        openStatus,
        notificationPermission,
      );
      if (openStatus && !notificationPermission?.enabled) {
        return closeAppPush();
      }
      if (openStatus && notificationPermission?.enabled) {
        return openAppPush();
      }
      if (settingRef.current) {
        settingRef.current = false;
        if (notificationPermission?.enabled && !openStatus) {
          await openAppPush();
          setAppOpenStatus(true);
        }
      }
    },
    [getPushSetting, closeAppPush, openAppPush],
  );

  useEffect(() => {
    updateNotificationPermission(notificationPermission);
  }, [updateNotificationPermission, notificationPermission]);

  const [disabled, setDisabled] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);

  useEffect(() => {
    setEnableAppPushSetting(appOpenStatus);
  }, [appOpenStatus]);

  const { enableNotification } = useSnapshot(UserStore);
  const notificationConfigs =
    (getItem(LOCAL_STORAGE_KEYS.NOTIFICATION_CONFIG) as Record<
      string,
      boolean
    >) || undefined;

  const handleCloseAppNotify = useMemoizedFn(async () => {
    setModalVisible(false);
    await closeAppPush();
    await sleep(3000);
    updateNotificationPermission({ enabled: true });
    setDisabled(false);
  });

  const handleOpenAppNotify = async () => {
    setAppOpenStatus(true);
    if (notificationPermission?.enabled) {
      await openAppPush();
      // 等待 3 秒后获取推送设置，避免后端异步操作未完成
      await sleep(3000);
      await updateNotificationPermission({ enabled: true });
      setDisabled(false);
      return;
    }
    // requestOpenPushRef.current = true;
    log.trace("[PUSH_PERMISSION] open-settings", {
      action: "request-open-push",
    });
    const res = await requestNotificationPermission();
    if (res?.enabled) {
      log.debug(
        "[NotificationSection] handleOpenNotify res?.enabled",
        res?.enabled,
      );
      await openAppPush();
      log.debug("[NotificationSection] handleOpenNotify openAppPush");
      await sleep(3000);
      log.debug(
        "[NotificationSection] handleOpenNotify updateNotificationPermission",
      );
      updateNotificationPermission({ enabled: true });
      setDisabled(false);
      return;
    }
    if (res?.action === "open-settings") {
      settingRef.current = true;
      setDisabled(false);
      return;
    }
    // await updateNotificationPermission();
    setDisabled(false);
    return;
  };
  const handleOpenNotify = async () => {
    if (!UserStore.ownerInfo?.id || !notificationConfigs) {
      return;
    }
    setItem(LOCAL_STORAGE_KEYS.NOTIFICATION_CONFIG, {
      ...notificationConfigs,
      [UserStore.ownerInfo.id]: true,
    });
    UserStore.enableNotification = true;
    setDisabled(false);
  };

  const toggleNode = (
    <Switch
      checked={isApp ? enableAppPushSetting : enableNotification}
      disabled={disabled}
      onChange={(checked) => {
        try {
          if (!checked) {
            setModalVisible(true);
          } else {
            setDisabled(true);
            if (isApp) {
              handleOpenAppNotify();
              return;
            }

            handleOpenNotify();
          }
        } catch (error) {
          setDisabled(false);
          log.error("[Settings] handleOpenNotify error", error);
        }
      }}
    />
  );

  const confirmModal = (
    <SwapModal visible={modalVisible} onClose={() => setModalVisible(false)}>
      <div className="flex flex-col w-[320px] h-[172px] px-6 pt-6 pb-3 bg-background_solid_secondary rounded-4">
        <div className="headline_regular_s text-content_primary">
          {I18n.t("Turn Off Personalized Alerts?")}
        </div>
        <div className="body_m text-content_secondary mt-2">
          {I18n.t(
            "You'll lose tailored updates, and notice market moves too late.",
          )}
        </div>
        <div className="flex flex-row gap-2 mt-6 justify-end">
          {/* Cancel 保持实心 primary；仅修描边 Confirm 边框 token（lZBQgHailkL0 picked_dom） */}
          <button
            aria-label="Notification Section"
            data-openreplay-label={`Notification Section Button | ${I18n.t("Cancel")}`}
            id="btn-notification-section"
            className="sm_button button primary_button text-content_inverse_primary button_m group min-w-[78px]"
            onClick={() => {
              setModalVisible(false);
              setDisabled(false);
            }}
          >
            {I18n.t("Cancel")}
          </button>
          <button
            aria-label="Notification Section"
            data-openreplay-label={`Notification Section Button | ${I18n.t("Confirm")}`}
            id="btn-notification-section-2"
            className="sm_button button button_m group min-w-[78px] ring-1 ring-border_inverse_transparent20 text-content_primary md:not-loading-and-disabled:hover:bg-background_white_overlay5"
            onClick={async () => {
              setAppOpenStatus(false);
              if (isApp) {
                handleCloseAppNotify();
                return;
              }
              log.debug(
                "[NotificationConfig] handleCloseNotify notificationConfigs",
                JSON.stringify(notificationConfigs),
              );
              log.debug(
                "[NotificationConfig] handleCloseNotify UserStore.ownerInfo",
                JSON.stringify(UserStore.ownerInfo),
              );
              if (!UserStore.ownerInfo?.id || !notificationConfigs) {
                return;
              }
              setItem(LOCAL_STORAGE_KEYS.NOTIFICATION_CONFIG, {
                ...notificationConfigs,
                [UserStore.ownerInfo.id]: false,
              });
              log.debug(
                "[NotificationConfig] handleCloseNotify setItem notificationConfigs",
                JSON.stringify({
                  ...notificationConfigs,
                  [UserStore.ownerInfo.id]: false,
                }),
              );
              UserStore.enableNotification = false;
              setModalVisible(false);
              setDisabled(false);
            }}
          >
            {I18n.t("Confirm")}
          </button>
        </div>
      </div>
    </SwapModal>
  );

  if (variant === "inline") {
    return (
      <>
        {toggleNode}
        {confirmModal}
      </>
    );
  }

  return (
    <div className="flex flex-col gap-4 w-full p-6 border border-background_white_overlay10 rounded-xl">
      <div className="flex justify-between items-center w-full gap-2">
        <div className="flex flex-col gap-2">
          <div className="text-content_primary headline_regular_s">
            {I18n.t("Personalized Notifications")}
          </div>
          <div className="text-content_secondary body_s">
            {I18n.t(
              "Market timing decides winners. Get alerts built around your portfolio and never miss your moment.",
            )}
          </div>
        </div>

        {toggleNode}
        {confirmModal}
      </div>
    </div>
  );
};
